import { isAxiosError } from 'axios';
import type { UseFormReturn } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  type CreateDesignData,
  type Design,
  type DesignFilters,
  type DesignQuery,
  createDesign,
  updateDesign,
} from '../../api/design';
import { VestmentType, isV1VestmentType } from '../enum/vestmentType';
import { getAlbFeatures } from './alb';
import { getChasubleFeatures } from './chasuble';
import { getStoleFeatures } from './stole';

export const CENTER_FINISH_DESIGN_TYPES: (VestmentType | '')[] = [
  VestmentType.ChasubleCenterDesign,
  VestmentType.ChasubleTauOrphrey,
  VestmentType.ChasubleGothicStyle,
];

export type DesignFormValues = {
  code: string;
  vestmentType: VestmentType | '';
  image: File | null;
  centerFinish: string;
};

const DESIGN_FORM_FIELDS: (keyof DesignFormValues)[] = [
  'code',
  'vestmentType',
  'image',
  'centerFinish',
];

export function designToFormValues(
  design?: Design | null,
  inputValue?: string,
  onlyVestmentType?: VestmentType
): DesignFormValues {
  return {
    code: design?.code ?? inputValue ?? '',
    vestmentType: design?.vestmentType ?? onlyVestmentType ?? '',
    image: null,
    centerFinish: String(design?.centerFinish ?? ''),
  };
}

async function saveDesign(
  data: DesignFormValues,
  design?: Design | null
): Promise<Design> {
  const centerFinishInput = data.centerFinish.trim();
  const payload: CreateDesignData = {
    code: data.code.trim(),
    vestmentType: data.vestmentType as VestmentType,
    centerFinish:
      CENTER_FINISH_DESIGN_TYPES.includes(data.vestmentType) &&
      centerFinishInput
        ? Number(centerFinishInput)
        : null,
  };
  if (data.image) {
    payload.image = data.image;
  }
  return design ? updateDesign(design.id, payload) : createDesign(payload);
}

function showDesignSaveErrors(
  error: unknown,
  methods: UseFormReturn<DesignFormValues>
) {
  const errors: Record<string, string> | undefined = isAxiosError(error)
    ? error.response?.data?.errors
    : undefined;
  if (!errors) {
    toast.error('Failed to save design');
    return;
  }
  for (const [field, message] of Object.entries(errors)) {
    const name = DESIGN_FORM_FIELDS.find((f) => f === field);
    if (name) {
      methods.setError(name, { message });
    } else {
      toast.error(message);
    }
  }
}

/**
 * Creates the design, or updates it when one is given, with a success toast.
 * On failure, shows the errors on the form and rethrows.
 */
export async function submitDesign(
  data: DesignFormValues,
  design: Design | null | undefined,
  methods: UseFormReturn<DesignFormValues>
): Promise<Design> {
  try {
    const saved = await saveDesign(data, design);
    toast.success(
      design ? 'Design updated successfully' : 'Design created successfully'
    );
    return saved;
  } catch (error) {
    showDesignSaveErrors(error, methods);
    throw error;
  }
}

export const EMPTY_DESIGN_FILTERS: DesignFilters = {
  category: '',
  variant: '',
};

export function countActiveDesignFilters(filters: DesignFilters): number {
  let count = 0;
  if (filters.category) count += 1;
  if (filters.variant) count += 1;
  return count;
}

export function designFiltersToQueryParams(
  searchTerm: string,
  filters: DesignFilters
): DesignQuery {
  const query: DesignQuery = {};
  const search = searchTerm.trim();
  if (search) {
    query.search = search;
  }
  if (filters.category) {
    query.category = filters.category;
  }
  if (filters.variant) {
    query.variant = filters.variant;
  }
  return query;
}

export function designMatchesVestmentType(
  design: Pick<Design, 'vestmentType' | 'v1VestmentType'>,
  type: VestmentType
): boolean {
  const designTypes = getChasubleFeatures(type)?.designTypes ??
    getStoleFeatures(type)?.designTypes ??
    getAlbFeatures(type)?.designTypes ?? [type];
  return designTypes.some((designType) =>
    isV1VestmentType(designType)
      ? design.v1VestmentType === designType
      : design.vestmentType === designType
  );
}
